/***
 *
 *   TICKET LIST VIEW
 *   The list alternative to the kanban board. Ported from
 *   ui-reference/src/components/tickets/ticket-list-view.tsx over the shared
 *   DataTable (components/table/dataTable.js) rather than
 *   @tanstack/react-table, so there is no new dependency and the ticket,
 *   contact and user lists behave identically.
 *
 *   PROPS
 *   tickets: live-query result ({ data, status })
 *   onCreate: open create-ticket modal
 *   filtered: whether any toolbar filters are active (empty-state copy)
 *
 **********/

import { useMemo } from 'react';
import { DataTable, EmptyState, Icon, Tooltip, cn } from 'components/lib';
import { useSearchParams } from 'react-router-dom';
import { useTranslate } from 'app/translations';
import { PriorityBadge } from 'components/tickets/priorityBadge';
import { StatusBadge } from 'components/tickets/statusBadge';
import { AssigneeAvatars } from 'components/tickets/assigneeAvatars';
import { isTicketOverdue } from 'utilities/ticketDue';
import { findCurrStatus } from 'utilities/tickets';
import { formatShortDate } from 'utilities/date';
import Style from './listView.tailwind';

const PRIORITY_ORDER = { urgent: 0, high: 1, medium: 2, low: 3 };

function ticketSubject(ticket) {
    return ticket.subject || ticket.text || '';
}

function assigneeNames(ticket) {
    return (ticket.ticket_assignee || [])
        .map((a) => [a.first_name, a.last_name].filter(Boolean).join(' ') || a.email)
        .join(' ');
}

function commentCount(ticket) {
    return (ticket.histories || []).filter((h) => h.action_type === 'comment_posted').length;
}

export function TicketListView(props) {
    const { _t } = useTranslate();
    const [searchParams, setSearchParams] = useSearchParams();

    const rows = useMemo(
        () => (props.tickets?.data || []).filter((t) => !t.is_archived),
        [props.tickets?.data],
    );

    const isLoading = props.tickets?.status === 'loading' || props.tickets?.isLoading;

    function openTicket(ticket) {
        searchParams.set('id', ticket.number);
        setSearchParams(searchParams);
    }

    function DueDate({ ticket }) {
        if (!ticket.due_date) return <span className={Style.muted}>—</span>;

        const overdue = isTicketOverdue({
            due_date: ticket.due_date,
            status: findCurrStatus(ticket),
        });

        return (
            <span className={cn(Style.due, overdue ? Style.dueOverdue : Style.dueNormal)}>
                <Icon image="calendar" size={12} color="currentColor" />
                {formatShortDate(ticket.due_date)}
            </span>
        );
    }

    const columns = useMemo(
        () => [
            {
                key: 'number',
                header: _t('table.ticketNumber'),
                sortable: true,
                width: 120,
                render: (t) => <span className={Style.number}>{t.number}</span>,
            },
            {
                key: 'subject',
                header: _t('ticket.list.subject'),
                sortable: true,
                sortValue: (t) => ticketSubject(t).toLowerCase(),
                searchValue: ticketSubject,
                render: (t) => <span className={Style.subject}>{ticketSubject(t)}</span>,
            },
            {
                key: 'priority',
                header: _t('priority.label'),
                sortable: true,
                width: 110,
                sortValue: (t) => PRIORITY_ORDER[t.priority] ?? 9,
                searchValue: (t) => (t.priority ? _t(`priority.${t.priority}`) : ''),
                render: (t) => <PriorityBadge priority={t.priority} />,
            },
            {
                key: 'status',
                header: _t('ticket.list.status'),
                sortable: true,
                width: 130,
                sortValue: (t) => findCurrStatus(t) || '',
                searchValue: (t) => {
                    const key = findCurrStatus(t);
                    return key ? _t(`ticket.category.status.${key}`) : '';
                },
                render: (t) => <StatusBadge status={findCurrStatus(t)} />,
            },
            {
                key: 'assignees',
                header: _t('assignee.multiple'),
                width: 120,
                searchValue: assigneeNames,
                render: (t) =>
                    t.ticket_assignee?.length ? (
                        <AssigneeAvatars assignees={t.ticket_assignee} max={3} />
                    ) : (
                        <span className={Style.muted}>—</span>
                    ),
            },
            {
                key: 'category',
                header: _t('ticket.list.category'),
                sortable: true,
                width: 140,
                sortValue: (t) => t.ticket_category?.name || '',
                searchValue: (t) => t.ticket_category?.name || '',
                render: (t) => (
                    <span className={Style.secondary}>{t.ticket_category?.name || '—'}</span>
                ),
            },
            {
                key: 'due_date',
                header: _t('ticket.list.dueDate'),
                sortable: true,
                width: 120,
                sortValue: (t) => (t.due_date ? new Date(t.due_date).getTime() : null),
                searchable: false,
                render: (t) => <DueDate ticket={t} />,
            },
            {
                key: 'comments',
                header: (
                    <Tooltip label={_t('comment.multiple')}>
                        <Icon image="message-square" size={14} color="currentColor" />
                    </Tooltip>
                ),
                align: 'right',
                width: 70,
                sortable: true,
                sortValue: commentCount,
                searchable: false,
                render: (t) => <span className={Style.muted}>{commentCount(t)}</span>,
            },
        ],
        [_t],
    );

    const renderCard = (ticket) => {
        const statusKey = findCurrStatus(ticket);

        return (
            <>
                <div className={Style.cardTop}>
                    <span className={Style.number}>{ticket.number}</span>
                    <PriorityBadge priority={ticket.priority} />
                </div>

                <p className={Style.cardSubject}>{ticketSubject(ticket)}</p>

                <div className={Style.cardMeta}>
                    <StatusBadge status={statusKey} />
                    <AssigneeAvatars assignees={ticket.ticket_assignee || []} max={2} />
                </div>

                <div className={Style.cardFooter}>
                    <span>{ticket.ticket_category?.name || '—'}</span>
                    <span className={Style.cardFooterRight}>
                        <DueDate ticket={ticket} />
                        <span className={Style.cardComments}>
                            <Icon image="message-square" size={12} color="currentColor" />
                            {commentCount(ticket)}
                        </span>
                    </span>
                </div>
            </>
        );
    };

    return (
        <DataTable
            scroll
            loading={isLoading}
            rows={rows}
            columns={columns}
            rowKey={(t) => t.id || t.number}
            onRowClick={openTicket}
            renderCard={renderCard}
            defaultSort={{ key: 'number', dir: 'desc' }}
            empty={
                <EmptyState
                    className={Style.empty}
                    bare
                    title={props.filtered ? _t('ticket.noTicketsFiltered') : _t('ticket.noTickets')}
                    description={
                        props.filtered
                            ? _t('ticket.noTicketsFilteredDescription')
                            : _t('ticket.noTicketsDescription')
                    }
                    buttonText={_t('ticket.create.title')}
                    action={props.onCreate}
                />
            }
        />
    );
}
